import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ApiError, errorText } from "@common/api";
import { getMe, getProfile, putProfile, queryKeys } from "../api/salvagers";
import { RequireAuth, type AdminModeState } from "@jkast90/kast-common/auth";
import { Button, ErrorState, Switch, TextField } from "@jkast90/kast-common/ui";
import { screens } from "../components/authScreens";

function Account({ adminMode }: { adminMode: AdminModeState }) {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: queryKeys.me, queryFn: getMe });
  const profile = useQuery({
    queryKey: queryKeys.profile,
    queryFn: getProfile,
    // 404 means no profile yet, which is not an error worth retrying.
    retry: false,
  });
  const [displayName, setDisplayName] = useState("");
  const save = useMutation({
    mutationFn: putProfile,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.profile, saved);
      queryClient.invalidateQueries({ queryKey: queryKeys.tables });
      setDisplayName("");
    },
  });

  const noProfileYet = profile.error instanceof ApiError && profile.error.status === 404;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (displayName.trim()) save.mutate(displayName.trim());
  };

  return (
    <>
      {me.error && (
        <ErrorState
          description={errorText(me.error, "Could not load your account.")}
          onRetry={() => void me.refetch()}
        />
      )}
      {me.data && (
        <dl>
          <dt>Email</dt>
          <dd>{me.data.email}</dd>
          <dt>Groups</dt>
          <dd>{me.data.groups.length ? me.data.groups.join(", ") : "none"}</dd>
        </dl>
      )}
      <h2>Profile</h2>
      {profile.data && (
        <p>
          Display name: <strong>{profile.data.display_name}</strong>
        </p>
      )}
      {noProfileYet && <p className="muted">No profile yet. Pick a display name.</p>}
      {profile.error && !noProfileYet && (
        <ErrorState
          description={errorText(profile.error, "Could not load your profile.")}
          onRetry={() => void profile.refetch()}
        />
      )}
      <form onSubmit={submit} className="row">
        <TextField
          wrapperClassName="grow"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Display name"
          maxLength={40}
          aria-label="Display name"
        />
        <Button variant="primary" type="submit" disabled={save.isPending || !displayName.trim()}>
          Save
        </Button>
      </form>
      {save.error && <p className="notice error">{errorText(save.error, "Could not save.")}</p>}
      {adminMode.isAdmin && (
        <>
          <h2>Admin</h2>
          <Switch
            label="Admin tools"
            description="Shows the Admin page and the diagnostic logs. Off, you see the site as a player does."
            checked={adminMode.unlocked}
            onChange={adminMode.setEnabled}
          />
        </>
      )}
    </>
  );
}

export default function AccountPage({ adminMode }: { adminMode: AdminModeState }) {
  return (
    <main className="page">
      <h1>Account</h1>
      <RequireAuth loading={screens.loading} prompt={screens.prompt}>
        <Account adminMode={adminMode} />
      </RequireAuth>
    </main>
  );
}
